import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Header } from '@sorare/core/src/components/seo/Header';
import { Departures } from '@sorare/core/src/components/seo/Team/Departures';
import { Squad as DumbSquad } from '@sorare/core/src/components/seo/Team/Squad';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { metadatas } from '@sorare/core/src/lib/seo/baseball';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { InjuryTag } from '@sorare/marketplace/src/components/player/InjuryTag';

import type {
  BaseballTeamOverviewQuery,
  BaseballTeamOverviewQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const Layout = styled.div`
  @media ${desktopAndAbove} {
    display: grid;
    grid-template-areas: 'main side';
    grid-template-columns: 1fr 400px;
  }
`;

const BASEBALL_TEAM_OVERVIEW_QUERY = gql`
  query BaseballTeamOverviewQuery($slug: String!) {
    team(slug: $slug) {
      slug
      name
      pictureSecondaryUrl
      styleGuideColor
      anyActivePlayers {
        nodes {
          slug
          displayName
          shirtNumber
          avatarPictureUrl
          age
          country {
            slug
            code
            threeLetterCode
          }
          ...InjuryTag_anyPlayer
          lastFifteenSo5Appearances
        }
      }
      ...Header_subscription
      ...Departures_TeamInterface
    }
  }
  ${InjuryTag.fragments.anyPlayer}
  ${Header.fragments.subscription}
  ${Departures.fragments.TeamInterface}
` as TypedDocumentNode<
  BaseballTeamOverviewQuery,
  BaseballTeamOverviewQueryVariables
>;

type AnyPlayer =
  BaseballTeamOverviewQuery['team']['anyActivePlayers']['nodes'][0];

const isBaseballPlayer = (
  player: AnyPlayer
): player is Extract<AnyPlayer, { __typename: 'BaseballPlayer' }> =>
  isType(player, 'BaseballPlayer');

const Overview = () => {
  const { slug } = useTypedParams<PageParams>();
  const { data, loading } = useQuery(BASEBALL_TEAM_OVERVIEW_QUERY, {
    variables: { slug },
  });

  useTitleAndDescription(
    metadatas.team.title,
    metadatas.team.description,
    {
      team: data?.team.name,
    },
    {
      img: data?.team.pictureSecondaryUrl,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data?.team) return null;

  const { team } = data;

  const blocks = {
    header: (
      <Header
        id={team.slug}
        variant="team"
        displayName={team.name}
        pictureUrl={team.pictureSecondaryUrl}
        color={team.styleGuideColor}
        subscription={team}
      />
    ),
    squad: (
      <DumbSquad
        displayName={team.name}
        stats={['injury', 'shirtNumber', 'l15']}
        headers={{
          injury: '',
          shirtNumber: '#',
          l15: FILTERS.lastFifteenAverageScore.alias,
        }}
        groups={[
          {
            players: [...team.anyActivePlayers.nodes]
              .filter(isBaseballPlayer)
              .sort(
                (a, b) =>
                  (b.lastFifteenSo5Appearances || 0) -
                  (a.lastFifteenSo5Appearances || 0)
              )
              .map(player => ({
                ...player,
                country: {
                  code: player.country.code.toLowerCase(),
                  threeLetterCode: player.country.threeLetterCode,
                },
                pictureUrl: player.avatarPictureUrl,
                to: generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                  params: { slug: player.slug },
                  sport: Sport.BASEBALL,
                }),
                stats: {
                  injury: (
                    <Horizontal>
                      <InjuryTag player={player} />
                    </Horizontal>
                  ),
                  shirtNumber: `#${player.shirtNumber}`,
                  l15: (
                    <BodyM as="p" bold>
                      <FormattedNumber
                        value={player.lastFifteenSo5Appearances || 0}
                        maximumFractionDigits={0}
                      />
                    </BodyM>
                  ),
                },
              })),
          },
        ]}
      />
    ),
  };

  return (
    <Section>
      {blocks.header}
      <Layout>
        {blocks.squad}
        <Departures team={team} />
      </Layout>
    </Section>
  );
};

export default Overview;
